import { useMemo, useState } from 'react';
import {
  Box,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';
import { RT } from '../../retention/theme/retentionTheme';
import { SoftChip } from '../../retention/components/ui';
import {
  AttendanceRing,
  BandLegend,
  DataCheckNote,
  EmptyBlock,
  GRADIENTS,
  GlassBar,
  GradientStatCard,
  LoadingBlock,
  Panel,
  PctPill,
  PercentBar,
  fmtDate,
  fmtMonth,
  fmtNum,
  fmtPct,
  numberSx,
} from './components/ui';
import { BandDonut, RankBars, TrendChart, WeekdayChart } from './components/charts';

const STATUS_COLORS = { Active: RT.success, 'Temporary Inactive': RT.warning, Upcoming: RT.info, Completed: '#7C3AED' };

const BATCH_COLUMNS = [
  { key: 'batchName', label: 'Batch', align: 'left' },
  { key: 'students', label: 'Students', align: 'right' },
  { key: 'completion', label: 'Sessions (done / total)', align: 'left' },
  { key: 'attendance', label: 'Avg attendance', align: 'left' },
  { key: 'present', label: 'Present / Absent', align: 'right' },
  { key: 'lowStudents', label: 'Below 75%', align: 'right' },
  { key: 'lastSessionDate', label: 'Last session', align: 'right' },
];

const compare = (key, order) => (a, b) => {
  const left = a[key] ?? (order === 'asc' ? Infinity : -Infinity);
  const right = b[key] ?? (order === 'asc' ? Infinity : -Infinity);
  const result = typeof left === 'string' && typeof right === 'string' ? left.localeCompare(right) : left > right ? 1 : left < right ? -1 : 0;
  return order === 'asc' ? result : -result;
};

function BatchTable({ rows, onOpen }) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'attendance', order: 'asc' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const filtered = useMemo(() => {
    const text = search.trim().toLowerCase();
    const list = text
      ? rows.filter((row) =>
          [row.batchName, row.serialNo, row.courseName, row.className, row.croName, String(row.batchId)].some((value) =>
            String(value || '').toLowerCase().includes(text),
          ),
        )
      : rows;
    return [...list].sort(compare(sort.key, sort.order));
  }, [rows, search, sort]);

  const visible = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const toggleSort = (key) => {
    setSort((prev) => ({ key, order: prev.key === key && prev.order === 'desc' ? 'asc' : 'desc' }));
    setPage(0);
  };

  return (
    <Stack gap={1.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          placeholder="Search batch, course, class, CRO…"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(0);
          }}
          sx={{ width: { xs: '100%', sm: 320 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon icon="solar:magnifer-linear" width={18} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ flex: 1 }} />
        <BandLegend />
      </Stack>
      <TableContainer sx={{ border: `1px solid ${RT.line}`, borderRadius: 3 }}>
        <Table size="small" sx={{ minWidth: 980 }}>
          <TableHead>
            <TableRow sx={{ '& th': { bgcolor: '#F8F9FC', fontWeight: 700, color: RT.ink2, fontSize: 12.5, py: 1.25 } }}>
              {BATCH_COLUMNS.map((column) => (
                <TableCell key={column.key} align={column.align}>
                  <TableSortLabel active={sort.key === column.key} direction={sort.key === column.key ? sort.order : 'desc'} onClick={() => toggleSort(column.key)}>
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((row) => (
              <TableRow
                key={row.batchId}
                hover
                onClick={() => onOpen(row)}
                sx={{ cursor: 'pointer', '& td': { borderColor: RT.line, py: 1.25 } }}
              >
                <TableCell sx={{ maxWidth: 300 }}>
                  <Stack direction="row" alignItems="center" gap={1.25}>
                    <Box sx={{ width: 4, alignSelf: 'stretch', borderRadius: 4, bgcolor: STATUS_COLORS[row.status] || RT.faint }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: RT.ink }} noWrap title={row.batchName}>
                        {row.batchName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: RT.muted }} noWrap component="div">
                        #{row.batchId} · {row.courseName || '—'} · {row.className || '—'}
                      </Typography>
                      <Stack direction="row" gap={0.75} sx={{ mt: 0.5 }}>
                        <SoftChip label={row.status} color={STATUS_COLORS[row.status] || RT.faint} sx={{ height: 20, fontSize: 11 }} />
                        {row.croName && <SoftChip icon="solar:user-rounded-bold" label={row.croName} color={RT.ink2} sx={{ height: 20, fontSize: 11 }} />}
                      </Stack>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell align="right" sx={{ ...numberSx, fontWeight: 700 }}>
                  {row.students}
                </TableCell>
                <TableCell sx={{ minWidth: 190 }}>
                  <Stack gap={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 700, ...numberSx }}>
                      {row.completedSessions} / {row.totalSessions}
                      <Typography component="span" variant="caption" sx={{ color: RT.muted, ml: 0.75 }}>
                        {fmtPct(row.completion)} done
                      </Typography>
                    </Typography>
                    <PercentBar value={row.completion} color="#6366F1" height={6} />
                  </Stack>
                </TableCell>
                <TableCell sx={{ minWidth: 170 }}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <PercentBar value={row.attendance} height={8} />
                    <PctPill value={row.attendance} size="small" />
                  </Stack>
                </TableCell>
                <TableCell align="right" sx={numberSx}>
                  <Typography component="span" sx={{ color: RT.success, fontWeight: 700, fontSize: 13 }}>
                    {fmtNum(row.present)}
                  </Typography>
                  <Typography component="span" sx={{ color: RT.faint, mx: 0.5 }}>
                    /
                  </Typography>
                  <Typography component="span" sx={{ color: RT.error, fontWeight: 700, fontSize: 13 }}>
                    {fmtNum(row.absent)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {row.lowStudents ? <SoftChip label={row.lowStudents} color={RT.error} sx={{ fontWeight: 800 }} /> : <Typography variant="caption" sx={{ color: RT.faint }}>0</Typography>}
                </TableCell>
                <TableCell align="right" sx={{ ...numberSx, color: RT.muted, fontSize: 12.5 }}>
                  {fmtDate(row.lastSessionDate)}
                  {row.unmarkedSessions > 0 && (
                    <Tooltip title="Completed sessions with no student attendance saved">
                      <Typography variant="caption" component="div" sx={{ color: RT.warning, fontWeight: 700 }}>
                        {row.unmarkedSessions} unmarked
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ color: RT.faint }}>
                  <Icon icon="solar:alt-arrow-right-linear" width={18} />
                </TableCell>
              </TableRow>
            ))}
            {!visible.length && (
              <TableRow>
                <TableCell colSpan={BATCH_COLUMNS.length + 1}>
                  <EmptyBlock title="No batches match" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={(_, value) => setPage(value)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Stack>
  );
}

function SubjectTable({ rows }) {
  if (!rows.length) return <EmptyBlock title="No subjects" />;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 700, color: RT.muted, fontSize: 12, borderColor: RT.line } }}>
            <TableCell>Subject</TableCell>
            <TableCell align="right">Batches</TableCell>
            <TableCell>Sessions</TableCell>
            <TableCell>Attendance</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.subjectId ?? row.subjectName} sx={{ '& td': { borderColor: RT.line } }}>
              <TableCell sx={{ fontWeight: 700 }}>{row.subjectName}</TableCell>
              <TableCell align="right" sx={numberSx}>
                {row.batches}
              </TableCell>
              <TableCell sx={{ minWidth: 150 }}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <PercentBar value={row.totalSessions ? (row.completedSessions / row.totalSessions) * 100 : 0} color="#6366F1" height={6} />
                  <Typography variant="caption" sx={{ ...numberSx, fontWeight: 700, color: RT.ink2 }}>
                    {row.completedSessions}/{row.totalSessions}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell sx={{ minWidth: 150 }}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <PercentBar value={row.attendance} height={7} />
                  <PctPill value={row.attendance} size="small" />
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function LowStudents({ rows, threshold, onOpen }) {
  if (!rows.length) {
    return <EmptyBlock icon="solar:medal-ribbons-star-bold-duotone" title={`No student is below ${threshold}%`} caption="Everyone with saved marks is on track" />;
  }
  return (
    <Stack divider={<Box sx={{ borderTop: `1px dashed ${RT.line}` }} />}>
      {rows.map((row) => (
        <Stack
          key={row.batchAssignmentId}
          direction="row"
          alignItems="center"
          gap={1.5}
          onClick={() => onOpen(row)}
          sx={{ py: 1.1, px: 0.5, cursor: 'pointer', borderRadius: 2, '&:hover': { bgcolor: '#FFF5F7' } }}
        >
          <AttendanceRing value={row.attendance} size={44} thickness={4.5} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
              {row.studentName || '—'}
            </Typography>
            <Typography variant="caption" sx={{ color: RT.muted }} noWrap component="div">
              {row.batchName} · Adm {row.admnNo}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ ...numberSx, color: RT.ink2, fontWeight: 700 }}>
            {row.present}/{row.marked}
          </Typography>
          <Icon icon="solar:alt-arrow-right-linear" width={16} color={RT.faint} />
        </Stack>
      ))}
    </Stack>
  );
}

export default function OverviewView({ data, loading, onOpenBatch, onOpenStudent }) {
  const summary = data?.summary;
  const [breakdown, setBreakdown] = useState('course');
  const firstLoad = loading && !data;

  const trend = useMemo(
    () => (data?.monthly || []).map((month) => ({ label: fmtMonth(month.month), sessions: month.sessions, attendance: month.attendance })),
    [data]
  );
  const rankRows = useMemo(
    () =>
      breakdown === 'course'
        ? (data?.courses || []).filter((row) => row.marked).map((row) => ({ name: row.courseName, attendance: row.attendance, hint: `${row.batches} batches · ${row.students} students` }))
        : (data?.batches || [])
            .filter((row) => row.marked)
            .sort((a, b) => b.attendance - a.attendance)
            .slice(0, 10)
            .map((row) => ({ name: row.batchName, attendance: row.attendance, hint: `${row.completedSessions} sessions · ${row.students} students`, batch: row })),
    [data, breakdown]
  );

  const cards = [
    {
      gradient: GRADIENTS.indigo,
      icon: 'solar:chart-square-bold-duotone',
      label: 'Overall attendance',
      value: fmtPct(summary?.attendance),
      sub: `${fmtNum(summary?.present)} present of ${fmtNum(summary?.marked)} marks`,
      footer: <GlassBar value={summary?.attendance} />,
    },
    {
      gradient: GRADIENTS.emerald,
      icon: 'solar:calendar-mark-bold-duotone',
      label: 'Sessions completed',
      value: `${fmtNum(summary?.completedSessions)} / ${fmtNum(summary?.totalSessions)}`,
      sub: `${fmtPct(summary?.completion)} of scheduled sessions done`,
      footer: <GlassBar value={summary?.completion} />,
    },
    {
      gradient: GRADIENTS.violet,
      icon: 'solar:layers-bold-duotone',
      label: 'Avg attendance per batch',
      value: fmtPct(summary?.avgBatchAttendance),
      sub: summary?.bestBatch ? `Best: ${summary.bestBatch.batchName} (${fmtPct(summary.bestBatch.attendance)})` : 'Every batch weighs the same',
    },
    {
      gradient: GRADIENTS.sky,
      icon: 'solar:user-id-bold-duotone',
      label: 'Avg attendance per student',
      value: fmtPct(summary?.avgStudentAttendance),
      sub: `${fmtNum(summary?.students)} students in ${fmtNum(summary?.batches)} batches`,
    },
    {
      gradient: GRADIENTS.teal,
      icon: 'solar:users-group-two-rounded-bold-duotone',
      label: 'Batches',
      value: fmtNum(summary?.batches),
      sub: `${fmtNum(summary?.activeBatches)} active right now`,
    },
    {
      gradient: GRADIENTS.rose,
      icon: 'solar:danger-triangle-bold-duotone',
      label: `Students below ${summary?.lowThreshold ?? 75}%`,
      value: fmtNum(summary?.lowStudents),
      sub: summary?.worstBatch ? `Lowest batch: ${summary.worstBatch.batchName} (${fmtPct(summary.worstBatch.attendance)})` : 'Need a follow-up call',
    },
    {
      gradient: GRADIENTS.amber,
      icon: 'solar:hourglass-line-bold-duotone',
      label: 'Sessions pending',
      value: fmtNum(summary?.pendingSessions),
      sub: 'Scheduled, not taken yet',
    },
    {
      gradient: GRADIENTS.slate,
      icon: 'solar:clipboard-remove-bold-duotone',
      label: 'Unmarked sessions',
      value: fmtNum(summary?.unmarkedSessions),
      sub: `Completed but no student marks · ${fmtNum(summary?.absent)} absences, ${fmtNum(summary?.na)} N/A`,
    },
  ];

  if (!loading && data && !data.batches?.length) {
    return (
      <Panel>
        <EmptyBlock title="No batches for these filters" caption="Try another course, status or date range" />
      </Panel>
    );
  }

  return (
    <Stack gap={2.5}>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' } }}>
        {cards.map((card) => (
          <GradientStatCard key={card.label} {...card} loading={firstLoad} />
        ))}
      </Box>

      <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' } }}>
        <Panel icon="solar:graph-new-up-bold-duotone" title="Monthly trend" subtitle="Sessions completed and attendance % per month">
          {firstLoad ? <LoadingBlock rows={6} /> : trend.length ? <TrendChart points={trend} /> : <EmptyBlock title="No completed sessions in this range" />}
        </Panel>
        <Panel icon="solar:pie-chart-2-bold-duotone" color="#7C3AED" title="Students by attendance" subtitle="Students currently in a batch">
          {firstLoad ? <LoadingBlock rows={6} /> : <BandDonut bands={data?.bands} />}
        </Panel>
      </Box>

      <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
        <Panel icon="solar:calendar-date-bold-duotone" color={RT.info} title="Attendance by weekday" subtitle="Which days students turn up least">
          {firstLoad ? <LoadingBlock /> : <WeekdayChart weekday={data?.weekday} />}
        </Panel>
        <Panel
          icon="solar:ranking-bold-duotone"
          color={RT.success}
          title={breakdown === 'course' ? 'Course wise attendance' : 'Top 10 batches'}
          action={
            <TextField select size="small" value={breakdown} onChange={(event) => setBreakdown(event.target.value)} sx={{ minWidth: 150 }}>
              <MenuItem value="course">Course wise</MenuItem>
              <MenuItem value="batch">Top batches</MenuItem>
            </TextField>
          }
        >
          {firstLoad ? (
            <LoadingBlock />
          ) : rankRows.length ? (
            <RankBars rows={rankRows} onClick={breakdown === 'batch' ? (row) => onOpenBatch(row.batch) : undefined} />
          ) : (
            <EmptyBlock title="No marks yet" />
          )}
        </Panel>
      </Box>

      <Panel
        icon="solar:users-group-rounded-bold-duotone"
        title="Batch wise attendance"
        subtitle="Click a batch for its subject, student and session breakdown"
      >
        {firstLoad ? <LoadingBlock rows={8} /> : <BatchTable rows={data?.batches || []} onOpen={onOpenBatch} />}
      </Panel>

      <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' } }}>
        <Panel icon="solar:book-2-bold-duotone" color="#0EA5E9" title="Subject wise (all batches)" subtitle="Sessions done and attendance per subject">
          {firstLoad ? <LoadingBlock /> : <SubjectTable rows={data?.subjects || []} />}
        </Panel>
        <Panel
          icon="solar:user-cross-rounded-bold-duotone"
          color={RT.error}
          title="Needs attention"
          subtitle={`Lowest attendance, below ${summary?.lowThreshold ?? 75}%`}
        >
          {firstLoad ? <LoadingBlock /> : <LowStudents rows={data?.lowStudents || []} threshold={summary?.lowThreshold ?? 75} onOpen={onOpenStudent} />}
        </Panel>
      </Box>
      <DataCheckNote check={data?.check} />
    </Stack>
  );
}
